import React from 'react';
import { ShieldExclamationIcon, ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

interface ConcentrationRisk {
  maxAssetWeight: number;
  herfindahlIndex: number;
  diversificationRatio: number;
}

interface RiskMetrics {
  portfolioVaR95: number;
  portfolioVaR99: number;
  expectedShortfall: number;
  beta: number;
  correlation: Record<string, number>;
  concentrationRisk: ConcentrationRisk;
}

interface RiskMetricsCardProps {
  riskData?: RiskMetrics;
  recommendations?: string[];
}

const RiskMetricsCard: React.FC<RiskMetricsCardProps> = ({ riskData, recommendations = [] }) => {
  if (!riskData) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading risk metrics...</p>
        </div>
      </div>
    );
  }

  const getRiskLevel = (value: number, thresholds: [number, number]) => {
    const [moderate, high] = thresholds;
    if (value >= high) return 'high';
    if (value >= moderate) return 'moderate';
    return 'low';
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'high': return 'text-red-600';
      case 'moderate': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getRiskBgColor = (level: string) => {
    switch (level) {
      case 'high': return 'bg-red-50 border-red-200';
      case 'moderate': return 'bg-yellow-50 border-yellow-200';
      case 'low': return 'bg-green-50 border-green-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getRiskIcon = (level: string) => {
    switch (level) {
      case 'high': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'moderate': return <ShieldExclamationIcon className="h-5 w-5 text-yellow-500" />;
      case 'low': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default: return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  // Risk level assessments
  const varRisk = getRiskLevel(Math.abs(riskData?.portfolioVaR95 || 0), [3, 5]);
  const concentrationLevel = getRiskLevel((riskData?.concentrationRisk?.maxAssetWeight || 0) * 100, [40, 60]);
  const betaRisk = getRiskLevel(Math.abs((riskData?.beta || 1) - 1), [0.5, 1.0]);

  const formatPercentage = (value: number | null | undefined) => `${Math.abs(value || 0).toFixed(2)}%`;
  const formatBeta = (value: number | null | undefined) => (value || 0).toFixed(2);
  const formatRatio = (value: number | null | undefined) => (value || 0).toFixed(2);

  return (
    <div className="space-y-6">
      {/* Value at Risk Section */}
      <div className={`border rounded-lg p-4 ${getRiskBgColor(varRisk)}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900">Value at Risk (VaR)</h4>
          {getRiskIcon(varRisk)}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {formatPercentage(riskData.portfolioVaR95)}
            </div>
            <div className="text-xs text-gray-600">VaR 95%</div>
            <div className="text-xs text-gray-500 mt-1">
              Max loss in 95% of cases
            </div>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {formatPercentage(riskData?.portfolioVaR99)}
            </div>
            <div className="text-xs text-gray-600">VaR 99%</div>
            <div className="text-xs text-gray-500 mt-1">
              Max loss in 99% of cases
            </div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Expected Shortfall (CVaR)</span>
            <span className="text-sm font-medium text-gray-900">
              {formatPercentage(riskData.expectedShortfall)}
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Average loss in worst 5% scenarios
          </div>
        </div>
      </div>

      {/* Portfolio Beta */}
      <div className={`border rounded-lg p-4 ${getRiskBgColor(betaRisk)}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900">Portfolio Beta</h4>
          {getRiskIcon(betaRisk)}
        </div>
        <div className="text-2xl font-bold text-gray-900 mb-2">
          {formatBeta(riskData?.beta)}
        </div>
        <div className="text-xs text-gray-500">
          {(riskData?.beta || 1) > 1 
            ? `${(((riskData?.beta || 1) - 1) * 100).toFixed(0)}% more volatile than market`
            : (riskData?.beta || 1) < 1
            ? `${((1 - (riskData?.beta || 1)) * 100).toFixed(0)}% less volatile than market`
            : 'Same volatility as market'
          }
        </div>
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${(riskData?.beta || 1) > 1.5 ? 'bg-red-500' : (riskData?.beta || 1) > 1.2 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min((riskData?.beta || 1) / 2 * 100, 100)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Conservative</span>
            <span>Aggressive</span>
          </div>
        </div>
      </div>

      {/* Concentration Risk */}
      <div className={`border rounded-lg p-4 ${getRiskBgColor(concentrationLevel)}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900">Concentration Risk</h4>
          {getRiskIcon(concentrationLevel)}
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Largest Position</span>
            <span className="text-sm font-medium text-gray-900">
              {formatPercentage((riskData?.concentrationRisk?.maxAssetWeight || 0) * 100)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Diversification Ratio</span>
            <span className="text-sm font-medium text-gray-900">
              {formatRatio(riskData?.concentrationRisk?.diversificationRatio)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Herfindahl Index</span>
            <span className="text-sm font-medium text-gray-900">
              {formatRatio(riskData?.concentrationRisk?.herfindahlIndex)}
            </span>
          </div>
        </div>
        
        {/* Concentration Risk Bar */}
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                (riskData?.concentrationRisk?.maxAssetWeight || 0) > 0.6 ? 'bg-red-500' : 
                (riskData?.concentrationRisk?.maxAssetWeight || 0) > 0.4 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${(riskData?.concentrationRisk?.maxAssetWeight || 0) * 100}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Well Diversified</span>
            <span>Concentrated</span>
          </div>
        </div>
      </div>

      {/* Risk Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Risk Assessment Summary</h4>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className={`text-lg font-semibold ${getRiskColor(varRisk)}`}>
              {varRisk.toUpperCase()}
            </div>
            <div className="text-xs text-gray-600">Market Risk</div>
          </div>
          <div>
            <div className={`text-lg font-semibold ${getRiskColor(concentrationLevel)}`}>
              {concentrationLevel.toUpperCase()}
            </div>
            <div className="text-xs text-gray-600">Concentration</div>
          </div>
          <div>
            <div className={`text-lg font-semibold ${getRiskColor(betaRisk)}`}>
              {betaRisk.toUpperCase()}
            </div>
            <div className="text-xs text-gray-600">Volatility</div>
          </div>
        </div>
      </div>

      {/* Asset Correlations */}
      {Object.keys(riskData.correlation).length > 0 && (
        <div className="border rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Key Correlations</h4>
          <div className="space-y-2">
            {Object.entries(riskData.correlation).slice(0, 5).map(([asset, correlation]) => {
              const corrValue = typeof correlation === 'number' ? correlation : parseFloat(correlation as string) || 0;
              return (
                <div key={asset} className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">{asset}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          Math.abs(corrValue) > 0.7 ? 'bg-red-500' : 
                          Math.abs(corrValue) > 0.3 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ 
                          width: `${Math.abs(corrValue) * 100}%`,
                          marginLeft: corrValue < 0 ? `${(1 + corrValue) * 100}%` : '0'
                        }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-gray-900 w-8">
                      {corrValue.toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risk Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center">
            <InformationCircleIcon className="h-4 w-4 mr-2" />
            Risk Management Tips
          </h4>
          <ul className="space-y-2">
            {recommendations.slice(0, 3).map((recommendation, index) => (
              <li key={index} className="text-xs text-blue-800 flex items-start">
                <div className="w-1 h-1 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                <span>{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RiskMetricsCard; 